/**
 * 라우트 가드 — RequireAuth, RequirePerm, RequireAdmin.
 *
 * <Route element={<RequireAuth />}> ... </Route> 패턴.
 * 자식 라우트는 <Outlet /> 으로 렌더된다.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { SplashMark } from "@/components/ui/SplashMark";
import { Login } from "@/pages/Login";
import { RouteState } from "./RouteState";
import { PERMISSION_LABEL } from "@/domain/menus";
import { isTenantKey } from "@/domain/space";

/**
 * 전체화면 로더 — 유리 질감 로고 마크(SplashMark) 1.05초 시퀀스 + 오른쪽 상호 워드마크(public/logo-title-color.svg) 한 줄.
 * 흰 글자 버전은 logo-title-noncolor.svg.
 *
 * 시퀀스 한 번(play) → 끝(full). 로딩이 그 뒤에도 이어지면 물방울·파문만 반복(loop)해 멈춘 것처럼 보이지 않게 한다.
 * done=true 이고 full 이면 onFilled 를 호출한다(재생 중이면 끝까지 재생한 뒤).
 * leaving=true 면 앱 화면 위에 덮인 채 서서히 사라진다.
 */
// ?v= : 2026-10-13 배포에서 이 경로가 HTML(1년 immutable 캐시)로 응답됐던 브라우저 캐시를 우회
const SPLASH_TITLE = "/logo-title-color.svg?v=20261015";
function FullScreenLoader({
  label = "통합관리시스템",
  done = false,
  leaving = false,
  onFilled,
}: {
  label?: string;
  done?: boolean;
  leaving?: boolean;
  onFilled?: () => void;
}) {
  const [titleReady, setTitleReady] = useState(false);
  const [cycle, setCycle] = useState<"play" | "full">("play");
  const firedRef = useRef(false);
  const fire = () => {
    if (firedRef.current) return;
    firedRef.current = true;
    onFilled?.();
  };
  // 애니메이션이 안 도는 환경(모션 줄이기 등)에서도 진입이 막히지 않도록 안전 타이머
  useEffect(() => {
    if (!done) return;
    // 모션 줄이기면 애니메이션 자체가 없으니 바로 진입
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const t = setTimeout(fire, reduce ? 0 : 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  // 이미 다 돈 상태에서 로딩이 끝나면 즉시 진입 단계로
  useEffect(() => {
    if (done && cycle === "full") fire();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, cycle]);

  return (
    <div className={`app-splash${leaving ? " is-leaving" : ""}`} role="status" aria-label={`${label} 불러오는 중`}>
      <div className="app-splash-logo">
        <SplashMark play loop={cycle === "full" && !done} onSequenceEnd={() => setCycle("full")} />
        <img
          className={`app-splash-title${titleReady ? " is-ready" : ""}`}
          src={SPLASH_TITLE}
          alt=""
          aria-hidden="true"
          onLoad={() => setTitleReady(true)}
        />
      </div>
    </div>
  );
}

/** 색이 끝까지 찬 뒤 HOLD 만큼 머물고, FADE 동안 로더를 페이드아웃 → 제거 */
const SPLASH_HOLD_MS = 300;
const SPLASH_FADE_MS = 450;
function useSplashPhase(loading: boolean) {
  // 마운트 시점에 로딩 중이었을 때만 스플래시 단계를 거친다.
  const [phase, setPhase] = useState<"splash" | "leaving" | "done">(loading ? "splash" : "done");
  const timers = useRef<number[]>([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  const onFilled = () => {
    timers.current.push(
      window.setTimeout(() => {
        setPhase("leaving");
        timers.current.push(window.setTimeout(() => setPhase("done"), SPLASH_FADE_MS));
      }, SPLASH_HOLD_MS),
    );
  };
  return { phase, onFilled };
}

export function RequireAuth() {
  const { user, loading } = useAuth();
  const { phase, onFilled } = useSplashPhase(loading);

  // 미로그인: /login?next= 으로 이동하지 않고 지금 주소에서 로그인 화면을 그린다(2026-11-02).
  // 주소창이 erp.jgind.co.kr/ 그대로 유지되고, 로그인되면 같은 자리에서 Outlet 이 열려 링크로 들어온 화면도 그대로 이어진다.
  if (!loading && !user) return <Login inline />;
  // 로더는 항상 두 번째 자식 자리 — Outlet 이 생겨도 같은 인스턴스가 유지된다(사이클 상태 보존).
  // 앱 화면은 불투명한 로더 뒤에서 먼저 그려지고, 로더만 페이드아웃된다.
  return (
    <>
      {!loading && user ? <Outlet /> : null}
      {phase !== "done" ? <FullScreenLoader done={!loading} leaving={phase === "leaving"} onFilled={onFilled} /> : null}
    </>
  );
}

/**
 * `perm`은 단일 키 또는 배열. 배열이면 anyOf — 하나라도 보유 시 통과.
 * /c/:tenant 아래면 그 법인 기준으로 판정(hasPerm(key, tenant) — 1단계는 결과 동일, 2단계 법인별 역할 자리).
 * 법인 소속 여부는 상위 TenantGate 가 이미 확인했다.
 */
export function RequirePerm({ perm }: { perm: string | string[] }) {
  const { user, loading, hasPerm } = useAuth();
  const location = useLocation();
  const { tenant: tenantParam } = useParams();
  const tenant = isTenantKey(tenantParam) ? tenantParam : undefined;

  if (loading) return <FullScreenLoader />;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  const keys = Array.isArray(perm) ? perm : [perm];
  if (!keys.some((k) => hasPerm(k, tenant))) {
    return (
      <RouteState kind="denied" description={<>
        이 페이지를 보려면 <strong>{keys.map((key) => PERMISSION_LABEL.get(key) ?? key).join(" 또는 ")} 권한</strong>이 필요해요.<br />
        업무에 필요한 화면이라면 관리자에게 문의해 주세요.
      </>} />
    );
  }
  return <Outlet />;
}

/**
 * 전체 관리자(claim is_admin → user.isAdmin)만 통과 — 통합관리 /group/* (설계 12 §8-B).
 * 메뉴 권한 키(admin.user.manage 등)는 menus.ts 에 없어 1단계는 claim 으로 판정한다.
 * 법인 관리자도 막는다(법인 관리자 지위는 법인 환경설정에서만 쓰임).
 * `denied` = 권한 없음 화면(B-6b). 생략하면 기본 안내.
 */
export function RequireAdmin({ denied }: { denied?: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <FullScreenLoader />;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (!user.isAdmin) {
    return (
      <>
        {denied ?? (
          <RouteState kind="denied" description="이 페이지는 전체 관리자만 볼 수 있어요. 관리자에게 문의해 주세요." />
        )}
      </>
    );
  }
  return <Outlet />;
}
